import React, { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash, faSpinner } from "@fortawesome/free-solid-svg-icons";
import SearchableAddDropdown from "./SearchableAddDropdown";
import {
  PERCENT_TOTAL_TOLERANCE,
  buildSplitsValue,
  formatArea,
  formatSplitAreasInline,
  getElementArea,
  parseSplitsValue,
  summarizeSplitAreas,
} from "../utils/percentSplits";

const getElementName = (el) =>
  el?.elementName || el?.element_name || `#${el?.id}`;

const normalizeKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const ToggleSwitch = ({ checked, onChange, disabled = false, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={onChange}
    className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
      checked ? "bg-blue-600" : "bg-gray-300"
    }`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
        checked ? "translate-x-4" : "translate-x-1"
      }`}
    />
  </button>
);

/**
 * Shared editor for percentage splits over an element's area — application
 * methods (`Swing-40;Robot-60`) and damage combinations (`C2A-60;C3B-40`) are
 * the same interaction: pick a value, give it a percentage, add more rows, total
 * 100, then choose which child elements it applies to.
 *
 * mode "area":   element is at the area-data level; the settings apply to it only.
 * mode "parent": element is one level above; toggle switches choose which
 *                area-data children receive the settings.
 *
 * Callers supply the vocabulary and wording; `sidePanel` is rendered as a
 * right-hand column when provided (access methods use it for the global
 * productivity adjustments matrix; damage combinations have no equivalent).
 */
const SplitAllocationModal = ({
  element,
  mode,
  targets,
  getLevelLabel,
  onSave,
  onClose,
  // vocabulary
  options,
  onAddOption = null,
  getElementValue,
  // wording
  title,
  itemLabel,
  itemLabelPlural,
  selectPlaceholder,
  describeIntro = null,
  summaryLabel,
  // extras
  sidePanel = null,
  banner = null,
  footNote = null,
}) => {
  const isParentMode = mode === "parent";
  const elementLevel = element.levelNumber || element.level_number;
  const levelLabel = getLevelLabel(elementLevel);
  const targetLevel = targets.length
    ? targets[0].levelNumber || targets[0].level_number
    : elementLevel + 1;
  const targetLevelLabel = getLevelLabel(targetLevel);

  const [rows, setRows] = useState(() => {
    let source = "";
    if (!isParentMode) {
      source = getElementValue(element);
    } else {
      const uniqueValues = Array.from(new Set(targets.map(getElementValue)));
      if (uniqueValues.length === 1) source = uniqueValues[0];
    }
    const parsed = parseSplitsValue(source);
    return parsed.length ? parsed : [{ name: "", percentage: 100 }];
  });
  const [selectedIds, setSelectedIds] = useState(
    () => new Set(targets.map((t) => t.id)),
  );
  const [saving, setSaving] = useState(false);
  const [saveErrors, setSaveErrors] = useState([]);

  // Current per-value areas across all children, before this edit (parent mode).
  const currentSummary = useMemo(
    () => (isParentMode ? summarizeSplitAreas(targets, getElementValue) : null),
    [isParentMode, targets, getElementValue],
  );

  // Area the edited split will be distributed over.
  const selectedArea = useMemo(() => {
    if (!isParentMode) return getElementArea(element);
    return targets
      .filter((t) => selectedIds.has(t.id))
      .reduce((sum, t) => sum + getElementArea(t), 0);
  }, [isParentMode, element, targets, selectedIds]);

  const totalPercent = useMemo(
    () =>
      Math.round(
        rows.reduce((sum, row) => sum + (Number(row.percentage) || 0), 0) * 100,
      ) / 100,
    [rows],
  );

  const validationErrors = useMemo(() => {
    const errors = [];

    if (rows.length > 0) {
      if (rows.some((row) => !String(row.name || "").trim())) {
        errors.push(`Every row needs ${itemLabel}.`);
      }
      if (
        rows.some((row) => {
          const pct = Number(row.percentage);
          return !(pct > 0) || pct > 100;
        })
      ) {
        errors.push("Each percentage must be greater than 0 and at most 100.");
      }
      const keys = rows
        .map((row) => normalizeKey(row.name))
        .filter(Boolean);
      if (new Set(keys).size !== keys.length) {
        errors.push(`The same ${itemLabel} cannot be used twice.`);
      }
      if (Math.abs(totalPercent - 100) > PERCENT_TOTAL_TOLERANCE) {
        errors.push(`Percentages must total 100 (currently ${totalPercent}).`);
      }
    }

    if (isParentMode && selectedIds.size === 0) {
      errors.push(`Select at least one ${targetLevelLabel.toLowerCase()}.`);
    }

    return errors;
  }, [rows, totalPercent, isParentMode, selectedIds, targetLevelLabel, itemLabel]);

  const isClearing = rows.length === 0;
  const canSave = !saving && validationErrors.length === 0;

  const updateRow = (index, patch) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    );
  };

  const removeRow = (index) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const addRow = () => {
    setRows((prev) => [...prev, { name: "", percentage: "" }]);
  };

  const toggleTarget = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const setAllTargets = (selected) => {
    setSelectedIds(selected ? new Set(targets.map((t) => t.id)) : new Set());
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSave) return;

    const elementIds = isParentMode
      ? targets.filter((t) => selectedIds.has(t.id)).map((t) => t.id)
      : [element.id];

    try {
      setSaving(true);
      setSaveErrors([]);
      await onSave({ elementIds, value: buildSplitsValue(rows) });
    } catch (err) {
      const details = err.response?.data?.details;
      setSaveErrors(
        Array.isArray(details) && details.length
          ? details
          : [
              err.response?.data?.error ||
                err.response?.data?.message ||
                `Failed to save ${itemLabelPlural}.`,
            ],
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className={`modal-content ${sidePanel ? "modal-xlarge" : "modal-large"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>
            {title} — {getElementName(element)} ({levelLabel})
          </h2>
          <button onClick={onClose} className="close-btn" aria-label="Close">
            ×
          </button>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-stretch">
          <form onSubmit={handleSubmit} className="modal-form flex-1 min-w-0">
            {/* Current per-value area summary across children (parent mode) */}
            {isParentMode && currentSummary && (
              <div className="mb-4 px-3 py-2 rounded bg-gray-50 border border-gray-200">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-1">
                  {summaryLabel} across {targets.length}{" "}
                  {targetLevelLabel.toLowerCase()}(s)
                </div>
                <div className="flex flex-wrap gap-2">
                  {currentSummary.entries.length === 0 &&
                    currentSummary.unassignedArea === 0 && (
                      <span className="text-sm text-gray-500">
                        No area data available.
                      </span>
                    )}
                  {currentSummary.entries.map(({ name, area }) => (
                    <span
                      key={name}
                      className="px-2 py-1 rounded bg-violet-100 text-violet-800 text-sm font-medium"
                    >
                      {name}: {formatArea(area)} m²
                    </span>
                  ))}
                  {currentSummary.unassignedArea > 0 && (
                    <span className="px-2 py-1 rounded bg-gray-200 text-gray-600 text-sm">
                      Unassigned: {formatArea(currentSummary.unassignedArea)} m²
                    </span>
                  )}
                </div>
              </div>
            )}

            <p className="text-sm text-gray-600 mb-3">
              {describeIntro ||
                (isParentMode
                  ? `Define the ${itemLabel} split, then choose the ${targetLevelLabel.toLowerCase()}(s) it applies to. Percentages must total 100.`
                  : `Define how the area of this element is split across ${itemLabelPlural}. Percentages must total 100.`)}
            </p>

            {banner}

            {/* Split rows */}
            <div className="flex flex-col gap-2 mb-2">
              {rows.map((row, index) => {
                const otherNames = new Set(
                  rows
                    .filter((_, i) => i !== index)
                    .map((r) => normalizeKey(r.name))
                    .filter(Boolean),
                );
                const rowOptions = options.filter(
                  (option) => !otherNames.has(normalizeKey(option)),
                );
                const rowArea =
                  (selectedArea * (Number(row.percentage) || 0)) / 100;

                return (
                  <div key={index} className="flex items-center gap-2">
                    <div className="flex-1">
                      <SearchableAddDropdown
                        value={row.name}
                        options={rowOptions}
                        onChange={(value) => updateRow(index, { name: value })}
                        onAddOption={onAddOption}
                        placeholder={selectPlaceholder}
                        disabled={saving}
                      />
                    </div>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min="0.01"
                        max="100"
                        step="0.01"
                        value={row.percentage}
                        onChange={(e) =>
                          updateRow(index, {
                            percentage:
                              e.target.value === ""
                                ? ""
                                : parseFloat(e.target.value),
                          })
                        }
                        disabled={saving}
                        className="w-24 px-2 py-2 border border-gray-300 rounded text-right"
                        aria-label="Percentage"
                      />
                      <span className="text-gray-500 text-sm">%</span>
                    </div>
                    <span className="w-32 text-right text-sm text-gray-600 tabular-nums">
                      = {formatArea(rowArea)} m²
                    </span>
                    <button
                      type="button"
                      onClick={() => removeRow(index)}
                      disabled={saving}
                      className="btn-icon btn-delete"
                      title={`Remove ${itemLabel}`}
                    >
                      <FontAwesomeIcon icon={faTrash} className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={addRow}
                  disabled={saving}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-1" />
                  Add {itemLabel}
                </button>
                {rows.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setRows([])}
                    disabled={saving}
                    className="text-sm text-red-600 hover:text-red-800"
                    title={`Clear all ${itemLabelPlural} for the selected ${targetLevelLabel.toLowerCase()}(s)`}
                  >
                    <FontAwesomeIcon icon={faTrash} className="mr-1" />
                    Clear all
                  </button>
                )}
              </div>
              {rows.length > 0 && (
                <span
                  className={`text-sm font-medium ${
                    Math.abs(totalPercent - 100) <= PERCENT_TOTAL_TOLERANCE
                      ? "text-green-700"
                      : "text-red-600"
                  }`}
                >
                  Total: {totalPercent}% of {formatArea(selectedArea)} m²
                </span>
              )}
            </div>

            {isClearing && (
              <div className="mb-4 px-3 py-2 rounded bg-amber-50 border border-amber-200 text-sm text-amber-800">
                No {itemLabelPlural} defined. Saving will clear the{" "}
                {itemLabelPlural} for the selected{" "}
                {targetLevelLabel.toLowerCase()}(s).
              </div>
            )}

            {footNote}

            {/* Target selection (parent mode only) */}
            {isParentMode && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="font-medium">
                    Apply to {targetLevelLabel.toLowerCase()}(s) (
                    {selectedIds.size}/{targets.length} selected)
                  </label>
                  <div className="flex gap-3 text-sm">
                    <button
                      type="button"
                      onClick={() => setAllTargets(true)}
                      disabled={saving}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      onClick={() => setAllTargets(false)}
                      disabled={saving}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Select none
                    </button>
                  </div>
                </div>
                <div className="max-h-64 overflow-y-auto border border-gray-200 rounded divide-y divide-gray-100">
                  {targets.map((target) => {
                    const currentAreas = formatSplitAreasInline(
                      getElementValue(target),
                      getElementArea(target),
                    );
                    return (
                      <div
                        key={target.id}
                        className="flex items-center gap-3 px-3 py-2"
                      >
                        <ToggleSwitch
                          checked={selectedIds.has(target.id)}
                          onChange={() => toggleTarget(target.id)}
                          disabled={saving}
                          label={`Apply to ${getElementName(target)}`}
                        />
                        <span className="font-medium text-sm whitespace-nowrap">
                          {getElementName(target)}
                        </span>
                        <span className="text-xs text-gray-400 whitespace-nowrap">
                          {formatArea(getElementArea(target))} m²
                        </span>
                        <span className="ml-auto text-xs text-gray-500 truncate max-w-[50%]">
                          {currentAreas || `No ${itemLabelPlural} set`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {(validationErrors.length > 0 || saveErrors.length > 0) && (
              <div className="mb-4 px-3 py-2 rounded bg-red-50 border border-red-200 text-sm text-red-700">
                <ul className="list-disc list-inside">
                  {[...validationErrors, ...saveErrors].map((error, i) => (
                    <li key={i}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="modal-actions">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
                disabled={saving}
              >
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={!canSave}>
                {saving ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </button>
            </div>
          </form>

          {sidePanel}
        </div>
      </div>
    </div>
  );
};

export default SplitAllocationModal;
